/**
 * Helper function to normalize strings for comparison and storage
 * Uses NFD normalization to handle accented characters properly
 * Examples: "Hermès" → "hermes", "Café de Paris" → "cafe de paris"
 * Format: lowercase, no accents, single spaces between words
 */
export const normalizeString = (str: string): string => {
  return str
    .toLowerCase()
    .normalize('NFD') // Decompose accented characters (e.g., è → e + ̀)
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim() // Remove leading/trailing spaces
}

/**
 * Helper function to sanitize user input text
 * Trims leading/trailing whitespace and collapses multiple spaces to single space
 * Examples: "  Hello   World  " → "Hello World"
 * Use this for cleaning user input before saving to database
 */
export const sanitizeText = (str: string): string => {
  return str.trim().replace(/\s+/g, ' ')
}

/**
 * Generates a URL-friendly slug from a string
 * Uses NFD normalization to handle accented characters
 * Format: lowercase, spaces replaced with hyphens, special characters removed
 */
export const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD') // Decompose accented characters
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
}

/**
 * Generates a URL-friendly slug from brand name and fragrance name
 * Format: brand-name-fragrance-name (lowercase, spaces replaced with hyphens)
 */
export const generateFragranceSlug = (brandName: string, fragranceName: string): string => {
  const combined = `${brandName}-${fragranceName}`
  return generateSlug(combined)
}
